import { normalizeGradingTreePayload } from '../components/Workspace/admin/grading/utils';
import type { GradingRole, GradingTreePayload } from '../components/Workspace/admin/grading/types';
import {
  buildGradingMatrix,
  matrixCellKey,
  matrixSelectionKey,
} from '../components/Workspace/employeeGrading/employeeGradingEngine';
import { fetchCommunityGradingTreeCached } from '../services/gradingService';
import {
  savePositionGrading,
  type MatrixSelections,
  type PositionGrading,
} from '../services/localEmployeeGradingStore';
import { upsertLocalEmployee, type LocalEmployee } from '../services/localEmployeesStore';

export type SeedMyEmployeesResult = {
  employeesUpserted: number;
  gradingsUpserted: number;
  gradingSkipped: boolean;
};

type FullEmployeeSeed = {
  id: string;
  fullName: string;
  position: string;
  fixMonthlyGross: number;
  stiFraction: number;
  ltip: boolean;
  roleHints: string[];
};

type UngradedEmployeeSeed = {
  id: string;
  fullName: string;
  position: string;
  fixMonthlyGross: number;
  stiFraction: number;
  ltip: boolean;
};

const FULL_EMPLOYEES: FullEmployeeSeed[] = [
  {
    id: 'test-seed-full-1',
    fullName: 'Новиков Артём Павлович',
    position: 'Аналитик M&A',
    fixMonthlyGross: 245_000,
    stiFraction: 0.18,
    ltip: false,
    roleHints: ['аналитик'],
  },
  {
    id: 'test-seed-full-2',
    fullName: 'Смирнова Екатерина Владимировна',
    position: 'Старший аналитик',
    fixMonthlyGross: 385_000,
    stiFraction: 0.25,
    ltip: true,
    roleHints: ['ассоциат', 'associate'],
  },
  {
    id: 'test-seed-full-3',
    fullName: 'Козлов Дмитрий Сергеевич',
    position: 'Директор по инвестициям',
    fixMonthlyGross: 625_000,
    stiFraction: 0.35,
    ltip: true,
    roleHints: ['директор'],
  },
  {
    id: 'test-seed-full-4',
    fullName: 'Волкова Анна Игоревна',
    position: 'Вице-президент',
    fixMonthlyGross: 890_000,
    stiFraction: 0.42,
    ltip: true,
    roleHints: ['вице-президент', 'vice president', 'vp'],
  },
];

const UNGRADED_EMPLOYEES: UngradedEmployeeSeed[] = [
  {
    id: 'test-seed-empty-1',
    fullName: 'Романов Илья Андреевич',
    position: 'Младший аналитик',
    fixMonthlyGross: 195_000,
    stiFraction: 0.15,
    ltip: false,
  },
  {
    id: 'test-seed-empty-2',
    fullName: 'Лебедева Ольга Николаевна',
    position: 'Менеджер проектов',
    fixMonthlyGross: 310_000,
    stiFraction: 0.22,
    ltip: false,
  },
  {
    id: 'test-seed-empty-3',
    fullName: 'Морозов Павел Викторович',
    position: 'Руководитель направления IR',
    fixMonthlyGross: 520_000,
    stiFraction: 0.3,
    ltip: true,
  },
];

function isRoleVisible(role: GradingRole): boolean {
  return role.is_visible !== false;
}

function findRoleByHints(tree: GradingTreePayload, hints: string[], usedRoleIds: Set<number>): GradingRole | null {
  for (const hint of hints) {
    const normalizedHint = hint.toLowerCase();
    const match = tree.roles.find(
      (role) => isRoleVisible(role)
        && !usedRoleIds.has(role.id)
        && role.name.toLowerCase().includes(normalizedHint),
    );
    if (match) return match;
  }
  return tree.roles.find((role) => isRoleVisible(role) && !usedRoleIds.has(role.id)) ?? null;
}

function familyIdForRole(role: GradingRole, tree: GradingTreePayload): number | null {
  const cluster = tree.roleClusters.find((item) => item.id === role.role_cluster_id);
  return cluster?.family_id ?? null;
}

function buildSelectionsForRole(role: GradingRole, tree: GradingTreePayload): MatrixSelections {
  const familyId = familyIdForRole(role, tree);
  if (familyId == null) return {};

  const matrix = buildGradingMatrix(familyId, tree);
  const selections: MatrixSelections = {};
  const clusterId = role.role_cluster_id;

  matrix.rows.forEach((row) => {
    const options = matrix.cells[matrixCellKey(row.factor.id, row.questionIndex, clusterId)]?.options ?? [];
    if (options.length === 0) return;
    const picked = options[Math.min(options.length - 1, Math.floor(options.length / 2))];
    selections[matrixSelectionKey(row.factor.id, row.questionIndex)] = picked.answerId;
  });

  return selections;
}

function buildPositionGrading(position: string, role: GradingRole, tree: GradingTreePayload, now: string): PositionGrading {
  const cluster = tree.roleClusters.find((item) => item.id === role.role_cluster_id);
  const family = cluster ? tree.families.find((item) => item.id === cluster.family_id) : null;
  if (!cluster || !family) {
    throw new Error(`Не удалось построить грейд для роли «${role.name}»`);
  }

  return {
    position,
    familyId: family.id,
    familyName: family.name,
    roleClusterId: cluster.id,
    roleClusterName: cluster.name,
    selections: buildSelectionsForRole(role, tree),
    roleId: role.id,
    roleName: role.name,
    gradeLevel: role.grade_level,
    updatedAt: now,
  };
}

export async function seedMyEmployeesTestData(): Promise<SeedMyEmployeesResult> {
  const now = new Date().toISOString();
  let gradingsUpserted = 0;
  let gradingSkipped = false;

  let tree: GradingTreePayload | null = null;
  try {
    tree = normalizeGradingTreePayload(await fetchCommunityGradingTreeCached());
  } catch (error) {
    console.error(error);
    gradingSkipped = true;
  }

  const usedRoleIds = new Set<number>();
  const positionGradings = new Map<string, PositionGrading>();

  for (const seed of FULL_EMPLOYEES) {
    const role = tree ? findRoleByHints(tree, seed.roleHints, usedRoleIds) : null;

    if (role && tree) {
      usedRoleIds.add(role.id);
      const positionKey = seed.position.trim().toLowerCase();
      if (!positionGradings.has(positionKey)) {
        positionGradings.set(positionKey, buildPositionGrading(seed.position, role, tree, now));
      }
    }

    const employee: LocalEmployee = {
      id: seed.id,
      fullName: seed.fullName,
      position: seed.position,
      fixMonthlyGross: seed.fixMonthlyGross,
      stiFraction: seed.stiFraction,
      ltip: seed.ltip,
      createdAt: now,
      updatedAt: now,
    };
    await upsertLocalEmployee(employee);
  }

  for (const seed of UNGRADED_EMPLOYEES) {
    const employee: LocalEmployee = {
      id: seed.id,
      fullName: seed.fullName,
      position: seed.position,
      fixMonthlyGross: seed.fixMonthlyGross,
      stiFraction: seed.stiFraction,
      ltip: seed.ltip,
      createdAt: now,
      updatedAt: now,
    };
    await upsertLocalEmployee(employee);
  }

  if (tree && positionGradings.size > 0) {
    for (const grading of Array.from(positionGradings.values())) {
      await savePositionGrading(grading);
      gradingsUpserted += 1;
    }
  } else if (!tree) {
    gradingSkipped = true;
  }

  return {
    employeesUpserted: FULL_EMPLOYEES.length + UNGRADED_EMPLOYEES.length,
    gradingsUpserted,
    gradingSkipped,
  };
}
